import { Alert, Box } from "@mui/material";
import type { GridPaginationModel, GridSortModel } from "@mui/x-data-grid";
import { useSnackbar } from "notistack";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { SimplifiedImportWizard } from "../../components/simplified-import-wizard";
import { useApiMutation } from "../../hooks/use-api-mutation";
import { useUrlModal } from "../../hooks/use-url-dialog";
import { useUrlFilters } from "../../hooks/use-url-filters";
import { BatchPriorityDialog } from "./batch-priority-dialog";
import { BatchSeverityDialog } from "./batch-severity-dialog";
import { BatchTagsDialog } from "./batch-tags-dialog";
import { DeleteConfirmationDialog } from "./delete-confirmation-dialog";
import { FloatingActionBar } from "./floating-action-bar";
import { PromoteDialog } from "./promote-dialog";
import { TestCasesDataGrid } from "./test-cases-data-grid";
import { TestCasesHeader } from "./test-cases-header";
import type { FilterType, TestCase } from "./types";
import { useTestCasesData } from "./use-test-cases-data";
import { useTestCasesFiltering } from "./use-test-cases-filtering";

// Stable defaults object (defined outside component to avoid re-creation on each render)
const FILTER_DEFAULTS = {
  search: "",
  type: "production" as FilterType,
  showRejected: false,
  tags: [] as string[],
  page: 0,
  pageSize: 25,
  sortField: "",
  sortDirection: "desc" as "asc" | "desc",
};

export default function TestCasesPage() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  // URL-based modal state (fixes back button behavior)
  const [importDialogOpen, setImportDialogOpen] = useUrlModal("import");
  const [promoteDialogOpen, setPromoteDialogOpen] = useUrlModal("promote");
  const [deleteDialogOpen, setDeleteDialogOpen] = useUrlModal("delete");
  const [priorityDialogOpen, setPriorityDialogOpen] = useUrlModal("priority");
  const [severityDialogOpen, setSeverityDialogOpen] = useUrlModal("severity");
  const [tagsDialogOpen, setTagsDialogOpen] = useUrlModal("tags");

  // URL-based filter state (persists across navigation)
  const [filters, setFilters] = useUrlFilters(FILTER_DEFAULTS);

  // DEBUG: Log when filters change
  useEffect(() => {
    console.log("[DEBUG] filters changed:", {
      page: filters.page,
      pageSize: filters.pageSize,
      sortField: filters.sortField,
      sortDirection: filters.sortDirection,
      search: filters.search,
    });
  }, [filters]);

  // Derive pagination and sort models from URL state
  const paginationModel = useMemo<GridPaginationModel>(
    () => ({
      page: filters.page,
      pageSize: filters.pageSize,
    }),
    [filters.page, filters.pageSize],
  );

  const sortModel = useMemo<GridSortModel>(
    () => (filters.sortField ? [{ field: filters.sortField, sort: filters.sortDirection }] : []),
    [filters.sortField, filters.sortDirection],
  );

  const handlePaginationModelChange = useCallback(
    (model: GridPaginationModel) => {
      console.log("[DEBUG] handlePaginationModelChange called:", {
        incoming: model,
        current: paginationModel,
      });

      // Guard against unnecessary updates
      if (model.page === paginationModel.page && model.pageSize === paginationModel.pageSize) {
        console.log("[DEBUG] Pagination unchanged, skipping");
        return;
      }
      console.log("[DEBUG] Pagination changed, updating filters");
      setFilters({ page: model.page, pageSize: model.pageSize });
    },
    [setFilters, paginationModel],
  );

  const handleSortModelChange = useCallback(
    (model: GridSortModel) => {
      const newField = model.length > 0 ? model[0].field : "";
      const newDirection = model.length > 0 ? (model[0].sort ?? "desc") : "desc";
      const currentField = sortModel.length > 0 ? sortModel[0].field : "";
      const currentDirection = sortModel.length > 0 ? (sortModel[0].sort ?? "desc") : "desc";

      console.log("[DEBUG] handleSortModelChange called:", {
        incoming: model,
        newField,
        newDirection,
        currentField,
        currentDirection,
      });

      // Guard against unnecessary updates
      if (newField === currentField && newDirection === currentDirection) {
        console.log("[DEBUG] Sort unchanged, skipping");
        return;
      }

      console.log("[DEBUG] Sort changed, updating filters");
      setFilters({
        sortField: newField,
        sortDirection: newDirection as "asc" | "desc",
      });
    },
    [setFilters, sortModel],
  );

  // Local state for dialog data (not URL-persisted)
  const [selectedTests, setSelectedTests] = useState<TestCase[]>([]);
  const [testsToPromote, setTestsToPromote] = useState<TestCase[]>([]);
  const [testsToDelete, setTestsToDelete] = useState<TestCase[]>([]);
  const [testsToUpdatePriority, setTestsToUpdatePriority] = useState<TestCase[]>([]);
  const [testsToUpdateSeverity, setTestsToUpdateSeverity] = useState<TestCase[]>([]);
  const [testsToUpdateTags, setTestsToUpdateTags] = useState<TestCase[]>([]);

  const { testCases, total, isLoading, error } = useTestCasesData({
    searchQuery: filters.search,
    paginationModel,
    sortModel,
    tags: filters.tags,
    filterType: filters.type,
  });

  // Handle search change with pagination reset
  const handleSearchChange = (search: string) => {
    setFilters({ search, page: 0 });
  };

  // Handle tags change with pagination reset
  const handleTagsChange = (tags: string[]) => {
    setFilters({ tags, page: 0 });
  };

  // Handle filter type change with pagination reset
  const handleFilterChange = (type: string) => {
    setFilters({ type, page: 0 });
  };

  const { filteredTestCases } = useTestCasesFiltering(testCases, filters.showRejected);

  const { mutate: promoteTests, isPending: isPromoting } = useApiMutation<
    { target_config_id: string; test_ids: string[] },
    void
  >("POST", "/tests/promote", {
    onSuccess: () => {
      enqueueSnackbar("Tests promoted successfully", {
        variant: "success",
        anchorOrigin: {
          horizontal: "right",
          vertical: "bottom",
        },
      });
      setPromoteDialogOpen(false);
      setSelectedTests([]);
      setTestsToPromote([]);
    },
    onError: (error) => {
      enqueueSnackbar(`Failed to promote tests: ${error.message}`, {
        variant: "error",
        anchorOrigin: {
          horizontal: "right",
          vertical: "bottom",
        },
      });
    },
  });

  const { mutate: deleteTest, isPending: isDeleting } = useApiMutation<void, void>(
    "DELETE",
    "/tests",
    {
      onSuccess: () => {
        // Success handling is done in the loop
      },
      onError: (error) => {
        enqueueSnackbar(`Failed to delete test: ${error.message}`, {
          variant: "error",
          anchorOrigin: {
            horizontal: "right",
            vertical: "bottom",
          },
        });
      },
    },
  );

  const { mutate: updateTestPriority, isPending: isUpdatingPriority } = useApiMutation<
    { name: string; priority: string },
    void
  >("PUT", "", {
    onSuccess: () => {
      // Success handling is done in the loop
    },
    onError: (error) => {
      enqueueSnackbar(`Failed to update priority: ${error.message}`, {
        variant: "error",
        anchorOrigin: {
          horizontal: "right",
          vertical: "bottom",
        },
      });
    },
  });

  const { mutate: updateTestSeverity, isPending: isUpdatingSeverity } = useApiMutation<
    { name: string; severity: string },
    void
  >("PUT", "", {
    onSuccess: () => {
      // Success handling is done in the loop
    },
    onError: (error) => {
      enqueueSnackbar(`Failed to update severity: ${error.message}`, {
        variant: "error",
        anchorOrigin: {
          horizontal: "right",
          vertical: "bottom",
        },
      });
    },
  });

  const { mutate: addTagsToTest, isPending: isAddingTags } = useApiMutation<
    { tags: string[] },
    void
  >("POST", "", {
    onSuccess: () => {
      // Success handling is done in the loop
    },
    onError: (error) => {
      enqueueSnackbar(`Failed to add tags: ${error.message}`, {
        variant: "error",
        anchorOrigin: {
          horizontal: "right",
          vertical: "bottom",
        },
      });
    },
  });

  const handleCreateTest = () => {
    navigate("/generate");
  };

  const handleSelectionChange = (newSelectedTests: TestCase[]) => {
    setSelectedTests(newSelectedTests);
  };

  const handlePromote = (tests: TestCase[]) => {
    setTestsToPromote(tests);
    setPromoteDialogOpen(true);
  };

  const handlePromoteConfirm = (configId: string) => {
    const testIds = testsToPromote
      .filter((test) => test.type === "test" && test.test_data?.id)
      .map((test) => test.test_data!.id);

    if (testIds.length > 0) {
      promoteTests({ target_config_id: configId, test_ids: testIds });
    }
  };

  const handleDelete = (tests: TestCase[]) => {
    setTestsToDelete(tests);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    const testIds = testsToDelete
      .filter((test) => test.type === "test" && test.test_data?.id)
      .map((test) => test.test_data!.id);

    if (testIds.length > 0) {
      let successCount = 0;
      let errorCount = 0;

      // Delete each test individually
      for (const testId of testIds) {
        try {
          await new Promise<void>((resolve, reject) => {
            deleteTest(
              { _url: `/tests/${testId}` },
              {
                onSuccess: () => {
                  successCount++;
                  resolve();
                },
                onError: (error) => {
                  errorCount++;
                  reject(error);
                },
              },
            );
          });
        } catch (error) {
          // Error is already handled in the mutation's onError
          console.error(`Failed to delete test ${testId}:`, error);
        }
      }

      // Show appropriate success/error messages
      if (successCount > 0) {
        enqueueSnackbar(`${successCount} test(s) deleted successfully`, {
          variant: "success",
          anchorOrigin: {
            horizontal: "right",
            vertical: "bottom",
          },
        });
      }

      if (errorCount > 0) {
        enqueueSnackbar(`${errorCount} test(s) failed to delete`, {
          variant: "error",
          anchorOrigin: {
            horizontal: "right",
            vertical: "bottom",
          },
        });
      }

      // Close dialog and reset state
      setDeleteDialogOpen(false);
      setSelectedTests([]);
      setTestsToDelete([]);
    }
  };

  const handleUpdatePriority = (tests: TestCase[]) => {
    setTestsToUpdatePriority(tests);
    setPriorityDialogOpen(true);
  };

  const handlePriorityConfirm = async (priority: string) => {
    const testIds = testsToUpdatePriority
      .filter((test) => test.type === "test" && test.test_data?.id)
      .map((test) => ({
        id: test.test_data!.id,
        name: test.test_data!.name,
      }));

    if (testIds.length > 0) {
      let successCount = 0;
      let errorCount = 0;

      // Update each test individually
      for (const { id, name } of testIds) {
        try {
          await new Promise<void>((resolve, reject) => {
            updateTestPriority(
              { _url: `/tests/${id}`, name, priority },
              {
                onSuccess: () => {
                  successCount++;
                  resolve();
                },
                onError: (error) => {
                  errorCount++;
                  reject(error);
                },
              },
            );
          });
        } catch (error) {
          console.error(`Failed to update priority for test ${id}:`, error);
        }
      }

      // Show appropriate success/error messages
      if (successCount > 0) {
        enqueueSnackbar(`${successCount} test(s) priority updated successfully`, {
          variant: "success",
          anchorOrigin: {
            horizontal: "right",
            vertical: "bottom",
          },
        });
      }

      if (errorCount > 0) {
        enqueueSnackbar(`${errorCount} test(s) failed to update priority`, {
          variant: "error",
          anchorOrigin: {
            horizontal: "right",
            vertical: "bottom",
          },
        });
      }

      // Close dialog and reset state
      setPriorityDialogOpen(false);
      setSelectedTests([]);
      setTestsToUpdatePriority([]);
    }
  };

  const handleUpdateSeverity = (tests: TestCase[]) => {
    setTestsToUpdateSeverity(tests);
    setSeverityDialogOpen(true);
  };

  const handleSeverityConfirm = async (severity: string) => {
    const testIds = testsToUpdateSeverity
      .filter((test) => test.type === "test" && test.test_data?.id)
      .map((test) => ({
        id: test.test_data!.id,
        name: test.test_data!.name,
      }));

    if (testIds.length > 0) {
      let successCount = 0;
      let errorCount = 0;

      // Update each test individually
      for (const { id, name } of testIds) {
        try {
          await new Promise<void>((resolve, reject) => {
            updateTestSeverity(
              { _url: `/tests/${id}`, name, severity },
              {
                onSuccess: () => {
                  successCount++;
                  resolve();
                },
                onError: (error) => {
                  errorCount++;
                  reject(error);
                },
              },
            );
          });
        } catch (error) {
          console.error(`Failed to update severity for test ${id}:`, error);
        }
      }

      // Show appropriate success/error messages
      if (successCount > 0) {
        enqueueSnackbar(`${successCount} test(s) severity updated successfully`, {
          variant: "success",
          anchorOrigin: {
            horizontal: "right",
            vertical: "bottom",
          },
        });
      }

      if (errorCount > 0) {
        enqueueSnackbar(`${errorCount} test(s) failed to update severity`, {
          variant: "error",
          anchorOrigin: {
            horizontal: "right",
            vertical: "bottom",
          },
        });
      }

      // Close dialog and reset state
      setSeverityDialogOpen(false);
      setSelectedTests([]);
      setTestsToUpdateSeverity([]);
    }
  };

  const handleUpdateTags = (tests: TestCase[]) => {
    setTestsToUpdateTags(tests);
    setTagsDialogOpen(true);
  };

  const handleTagsConfirm = async (tags: string[]) => {
    const testIds = testsToUpdateTags
      .filter((test) => test.type === "test" && test.test_data?.id)
      .map((test) => test.test_data!.id);

    if (testIds.length > 0) {
      let successCount = 0;
      let errorCount = 0;

      // Add tags to each test individually
      for (const testId of testIds) {
        try {
          await new Promise<void>((resolve, reject) => {
            addTagsToTest(
              { _url: `/tests/${testId}/tags`, tags },
              {
                onSuccess: () => {
                  successCount++;
                  resolve();
                },
                onError: (error) => {
                  errorCount++;
                  reject(error);
                },
              },
            );
          });
        } catch (error) {
          console.error(`Failed to add tags to test ${testId}:`, error);
        }
      }

      // Show appropriate success/error messages
      if (successCount > 0) {
        enqueueSnackbar(`${successCount} test(s) tags added successfully`, {
          variant: "success",
          anchorOrigin: {
            horizontal: "right",
            vertical: "bottom",
          },
        });
      }

      if (errorCount > 0) {
        enqueueSnackbar(`${errorCount} test(s) failed to add tags`, {
          variant: "error",
          anchorOrigin: {
            horizontal: "right",
            vertical: "bottom",
          },
        });
      }

      // Close dialog and reset state
      setTagsDialogOpen(false);
      setSelectedTests([]);
      setTestsToUpdateTags([]);
    }
  };

  if (error) {
    return (
      <Box>
        <Alert severity="error">Failed to load test cases. Please try again later.</Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        flexDirection: "column",
        display: "flex",
        overflow: "hidden",
        width: "100%",
        maxWidth: "100%",
      }}
    >
      <TestCasesHeader
        searchQuery={filters.search}
        onSearchChange={handleSearchChange}
        filterType={filters.type}
        onFilterChange={handleFilterChange}
        isLoading={isLoading}
        onCreateTest={handleCreateTest}
        showRejected={filters.showRejected}
        onShowRejectedChange={(showRejected) => setFilters({ showRejected })}
        selectedTags={filters.tags}
        onTagsChange={handleTagsChange}
      />

      <SimplifiedImportWizard open={importDialogOpen} onClose={() => setImportDialogOpen(false)} />

      <TestCasesDataGrid
        testCases={filteredTestCases}
        isLoading={isLoading}
        selectedTests={selectedTests}
        onSelectionChange={handleSelectionChange}
        total={total}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationModelChange}
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
      />

      <FloatingActionBar
        selectedTests={selectedTests}
        onDelete={handleDelete}
        onPromote={handlePromote}
        onUpdatePriority={handleUpdatePriority}
        onUpdateSeverity={handleUpdateSeverity}
        onUpdateTags={handleUpdateTags}
      />

      <PromoteDialog
        open={promoteDialogOpen}
        onClose={() => setPromoteDialogOpen(false)}
        onConfirm={handlePromoteConfirm}
        selectedTests={selectedTests}
        isLoading={isPromoting}
      />

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        selectedTests={selectedTests}
        isLoading={isDeleting}
      />

      <BatchPriorityDialog
        open={priorityDialogOpen}
        onClose={() => setPriorityDialogOpen(false)}
        onConfirm={handlePriorityConfirm}
        selectedTests={testsToUpdatePriority}
        isLoading={isUpdatingPriority}
      />

      <BatchSeverityDialog
        open={severityDialogOpen}
        onClose={() => setSeverityDialogOpen(false)}
        onConfirm={handleSeverityConfirm}
        selectedTests={testsToUpdateSeverity}
        isLoading={isUpdatingSeverity}
      />

      <BatchTagsDialog
        open={tagsDialogOpen}
        onClose={() => setTagsDialogOpen(false)}
        onConfirm={handleTagsConfirm}
        selectedTests={testsToUpdateTags}
        isLoading={isAddingTags}
      />
    </Box>
  );
}
